import {
  ContractualScheduleEntryFormValue,
  calculateWeekTotals,
} from './contractual-schedules-utils'
import { ContractualTimeSlot, ContractualDaySchedule } from '../../../../api/users'
import {
  formatHoursAsTime,
  dateInputToDateValue,
  dateToDateInputValue,
} from '../../../../utils/date'
import { addDays } from 'date-fns'

/**
 * Validation errors type for contractual schedules
 */
export type ContractualSchedulesValidationErrors = Record<string, string>

/**
 * Validates a single time slot
 * @param timeSlot - Time slot to validate
 * @param index - Index of the time slot in the day
 * @param dayName - Name of the day (for error messages)
 * @param scheduleIndex - Index of the schedule entry (for error messages)
 * @returns Validation errors for this time slot
 */
/**
 * Check if a time slot is complete (has both startHour and endHour)
 * Exported for use in transformation functions
 */
export const isTimeSlotComplete = (timeSlot: ContractualTimeSlot): boolean => {
  return (
    typeof timeSlot.startHour === 'number' &&
    !isNaN(timeSlot.startHour) &&
    typeof timeSlot.endHour === 'number' &&
    !isNaN(timeSlot.endHour)
  )
}

/**
 * Check if a time slot has at least one hour filled
 */
const hasTimeSlotAnyHour = (timeSlot: ContractualTimeSlot): boolean => {
  const hasStartHour = typeof timeSlot.startHour === 'number' && !isNaN(timeSlot.startHour)
  const hasEndHour = typeof timeSlot.endHour === 'number' && !isNaN(timeSlot.endHour)
  return hasStartHour || hasEndHour
}

export const validateContractualTimeSlot = (
  timeSlot: ContractualTimeSlot,
  index: number,
  dayName: string,
  scheduleIndex: number,
): ContractualSchedulesValidationErrors => {
  const errors: ContractualSchedulesValidationErrors = {}
  const basePath = `contractualSchedules.${scheduleIndex}.schedule.${dayName}.timeSlots.${index}`

  // If both startHour and endHour are empty, the slot is considered empty and will be ignored
  // No validation error in this case
  const hasAnyHour = hasTimeSlotAnyHour(timeSlot)
  if (!hasAnyHour) {
    return errors // Empty slot, no validation needed
  }

  // If at least one hour is filled, the other becomes required
  const hasStartHour = typeof timeSlot.startHour === 'number' && !isNaN(timeSlot.startHour)
  const hasEndHour = typeof timeSlot.endHour === 'number' && !isNaN(timeSlot.endHour)

  // Validate startHour (required if endHour is filled)
  if (!hasStartHour) {
    errors[`${basePath}.startHour`] = 'Heure de début requise'
  } else if (timeSlot.startHour < 0 || timeSlot.startHour > 24) {
    errors[`${basePath}.startHour`] = "L'heure de début doit être entre 0 et 24"
  }

  // Validate endHour (required if startHour is filled)
  if (!hasEndHour) {
    errors[`${basePath}.endHour`] = 'Heure de fin requise'
  } else if (timeSlot.endHour < 0 || timeSlot.endHour > 24) {
    errors[`${basePath}.endHour`] = "L'heure de fin doit être entre 0 et 24"
  }

  // Validate that startHour and endHour are not equal (only if both are filled)
  if (isTimeSlotComplete(timeSlot) && timeSlot.startHour === timeSlot.endHour) {
    errors[`${basePath}.endHour`] = "L'heure de fin ne peut pas être égale à l'heure de début"
  }

  return errors
}

/**
 * Validates a day schedule (checks for overlapping time slots and chronological order)
 * @param daySchedule - Day schedule to validate
 * @param dayName - Name of the day (for error messages)
 * @param scheduleIndex - Index of the schedule entry (for error messages)
 * @returns Validation errors for this day
 */
export const validateContractualDaySchedule = (
  daySchedule: ContractualDaySchedule,
  dayName: string,
  scheduleIndex: number,
): ContractualSchedulesValidationErrors => {
  const errors: ContractualSchedulesValidationErrors = {}
  const basePath = `contractualSchedules.${scheduleIndex}.schedule.${dayName}`

  if (!daySchedule.timeSlots || !Array.isArray(daySchedule.timeSlots)) {
    return errors
  }

  // Validate each time slot
  daySchedule.timeSlots.forEach((timeSlot, index) => {
    const slotErrors = validateContractualTimeSlot(timeSlot, index, dayName, scheduleIndex)
    Object.assign(errors, slotErrors)
  })

  // Check for overlapping time slots and ensure chronological order
  // Only consider complete slots (with both startHour and endHour)
  const completeSlots = daySchedule.timeSlots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => isTimeSlotComplete(slot))
    .map(({ slot, index }) => {
      const startHour = slot.startHour as number
      const endHour = slot.endHour as number
      // Normalize end hour so overnight slots (end < start) extend into the next day
      const normalizedEnd = endHour >= startHour ? endHour : endHour + 24
      return {
        slot,
        originalIndex: index,
        normalizedStart: startHour,
        normalizedEnd,
      }
    })

  if (completeSlots.length > 0) {
    const sortedSlots = [...completeSlots].sort((a, b) => a.normalizedStart - b.normalizedStart)

    // Check if slots are in chronological order
    for (let i = 0; i < completeSlots.length; i++) {
      const sortedSlot = sortedSlots[i]
      const currentSlot = completeSlots[i]
      if (sortedSlot && currentSlot && currentSlot.originalIndex !== sortedSlot.originalIndex) {
        errors[`${basePath}.timeSlots.${currentSlot.originalIndex}.startHour`] =
          'Les créneaux doivent être triés par ordre chronologique'
        break
      }
    }

    // Check for overlaps (only between complete slots)
    for (let i = 0; i < sortedSlots.length - 1; i++) {
      const currentSlot = sortedSlots[i]
      const nextSlot = sortedSlots[i + 1]

      if (!currentSlot || !nextSlot) continue

      if (currentSlot.normalizedEnd > nextSlot.normalizedStart) {
        errors[`${basePath}.timeSlots.${currentSlot.originalIndex}.endHour`] =
          'Ce créneau chevauche le créneau suivant'
        break
      }
    }
  }

  return errors
}

/**
 * Validates all contractual schedules entries
 * @param schedules - Array of contractual schedule entries
 * @returns Validation errors
 */
export const validateContractualSchedules = (
  schedules: ContractualScheduleEntryFormValue[],
): ContractualSchedulesValidationErrors => {
  const errors: ContractualSchedulesValidationErrors = {}

  if (!schedules || schedules.length === 0) {
    return errors
  }

  // Sort schedules by effectiveFrom to make validation easier
  const sortedSchedules = [...schedules].sort(
    (a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime(),
  )

  // Check that periods follow each other exactly (Monday to Sunday, then next Monday to Sunday, etc.)
  for (let i = 0; i < sortedSchedules.length - 1; i++) {
    const currentSchedule = sortedSchedules[i]
    const nextSchedule = sortedSchedules[i + 1]

    if (!currentSchedule || !nextSchedule) continue

    // Only validate continuity if current period has an end date (is not active)
    if (currentSchedule.effectiveTo) {
      // Calculate the Monday following the Sunday end date of current period
      const currentEndDate = dateInputToDateValue(currentSchedule.effectiveTo)
      const expectedNextStartDate = addDays(currentEndDate, 1)
      const expectedNextStartDateString = dateToDateInputValue(expectedNextStartDate)

      // Check if next period starts exactly on the expected Monday
      if (nextSchedule.effectiveFrom !== expectedNextStartDateString) {
        const originalIndex = schedules.indexOf(nextSchedule)
        if (originalIndex !== -1) {
          errors[`contractualSchedules.${originalIndex}.effectiveFrom`] =
            `La période doit commencer le lundi suivant le dimanche de la période précédente (${expectedNextStartDateString})`
        }
      }
    }
    // If current period has no effectiveTo (is active), it's the last period, so no continuity check needed
  }

  // Check if we have more than one active entry (effectiveTo = null)
  const activeEntries = schedules.filter(s => !s.effectiveTo)
  if (activeEntries.length > 1) {
    activeEntries.forEach(entry => {
      const index = schedules.indexOf(entry)
      if (index !== -1) {
        errors[`contractualSchedules.${index}.effectiveTo`] =
          'Une seule période peut être active (sans date de fin)'
      }
    })
  }

  // Check if the active entry is the most recent one (last in sorted array)
  const activeEntryIndex = sortedSchedules.findIndex(s => !s.effectiveTo)
  if (activeEntryIndex !== -1 && activeEntryIndex !== sortedSchedules.length - 1) {
    const activeEntry = sortedSchedules[activeEntryIndex]
    if (activeEntry) {
      const originalIndex = schedules.indexOf(activeEntry)
      if (originalIndex !== -1) {
        errors[`contractualSchedules.${originalIndex}.effectiveTo`] =
          'La période active doit être la plus récente (en dernière position)'
      }
    }
  }

  // Validate each schedule entry
  schedules.forEach((schedule, scheduleIndex) => {
    // Validate effectiveFrom
    if (!schedule.effectiveFrom) {
      errors[`contractualSchedules.${scheduleIndex}.effectiveFrom`] = 'Date de début requise'
    } else {
      const fromDate = new Date(schedule.effectiveFrom)
      if (fromDate.toString() === 'Invalid Date') {
        errors[`contractualSchedules.${scheduleIndex}.effectiveFrom`] = 'Date invalide'
      } else {
        // Validate that effectiveFrom is a Monday (getDay() === 1, where Sunday = 0, Monday = 1)
        const dayOfWeek = fromDate.getDay()
        if (dayOfWeek !== 1) {
          errors[`contractualSchedules.${scheduleIndex}.effectiveFrom`] =
            'La date de début doit être un lundi'
        }
      }
    }

    // Validate effectiveTo
    if (schedule.effectiveTo) {
      const toDate = new Date(schedule.effectiveTo)
      if (toDate.toString() === 'Invalid Date') {
        errors[`contractualSchedules.${scheduleIndex}.effectiveTo`] = 'Date invalide'
      } else {
        // Validate that effectiveTo is a Sunday (getDay() === 0) or null
        const dayOfWeek = toDate.getDay()
        if (dayOfWeek !== 0) {
          errors[`contractualSchedules.${scheduleIndex}.effectiveTo`] =
            'La date de fin doit être un dimanche'
        } else if (schedule.effectiveFrom && toDate <= new Date(schedule.effectiveFrom)) {
          errors[`contractualSchedules.${scheduleIndex}.effectiveTo`] =
            'La date de fin doit être postérieure à la date de début'
        }
      }
    }

    // Validate weeklyHoursWorked (required)
    if (
      schedule.weeklyHoursWorked === undefined ||
      schedule.weeklyHoursWorked === null ||
      typeof schedule.weeklyHoursWorked !== 'number' ||
      isNaN(schedule.weeklyHoursWorked)
    ) {
      errors[`contractualSchedules.${scheduleIndex}.weeklyHoursWorked`] = 'Heures/semaine requises'
    }

    // Validate monthlyHoursWorked (required)
    if (
      schedule.monthlyHoursWorked === undefined ||
      schedule.monthlyHoursWorked === null ||
      typeof schedule.monthlyHoursWorked !== 'number' ||
      isNaN(schedule.monthlyHoursWorked)
    ) {
      errors[`contractualSchedules.${scheduleIndex}.monthlyHoursWorked`] = 'Heures/mois requises'
    }

    // Validate that total hours from time slots equals weeklyHoursWorked only if useTimeSlots is true
    // Note: schedule.schedule est toujours présent dans le formulaire (même si vide), donc on peut l'utiliser directement
    if (
      schedule.useTimeSlots !== false &&
      typeof schedule.weeklyHoursWorked === 'number' &&
      !isNaN(schedule.weeklyHoursWorked) &&
      schedule.schedule // Vérifier que schedule existe (devrait toujours être le cas dans le formulaire)
    ) {
      const totalHoursFromSlots = calculateWeekTotals(schedule.schedule)
      // Use a small epsilon for floating point comparison
      if (Math.abs(totalHoursFromSlots - schedule.weeklyHoursWorked) > 0.01) {
        // Store error on schedule.totalMismatch to display it in the total row instead of on the weeklyHoursWorked field
        errors[`contractualSchedules.${scheduleIndex}.schedule.totalMismatch`] =
          `Le total des heures des créneaux (${formatHoursAsTime(totalHoursFromSlots)}) doit être égal aux heures/semaine (${formatHoursAsTime(schedule.weeklyHoursWorked)})`
      }
    }

    // Validate each day of the week only if useTimeSlots is true (or undefined, default true)
    // Note: schedule.schedule est toujours présent dans le formulaire (même si vide), donc on peut l'utiliser directement
    if (schedule.useTimeSlots !== false && schedule.schedule) {
      const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ] as const
      days.forEach(dayName => {
        const dayErrors = validateContractualDaySchedule(
          schedule.schedule[dayName],
          dayName,
          scheduleIndex,
        )
        Object.assign(errors, dayErrors)
      })
    }
  })

  // Check for overlapping periods
  for (let i = 0; i < sortedSchedules.length - 1; i++) {
    const currentSchedule = sortedSchedules[i]
    const nextSchedule = sortedSchedules[i + 1]

    if (!currentSchedule || !nextSchedule) continue

    const currentEndDate = currentSchedule.effectiveTo
      ? new Date(currentSchedule.effectiveTo)
      : null
    const nextStartDate = new Date(nextSchedule.effectiveFrom)

    // If current period has no end date, it automatically overlaps with all subsequent periods
    if (!currentEndDate) {
      const originalIndex = schedules.indexOf(currentSchedule)
      if (originalIndex !== -1) {
        errors[`contractualSchedules.${originalIndex}.effectiveTo`] =
          'Cette période chevauche les périodes suivantes'
      }
      break
    }

    // If current period's end date is after or equal to next period's start date
    if (currentEndDate >= nextStartDate) {
      const originalIndex = schedules.indexOf(currentSchedule)
      if (originalIndex !== -1) {
        errors[`contractualSchedules.${originalIndex}.effectiveTo`] =
          'Cette période chevauche la période suivante'
      }
      break
    }
  }

  return errors
}
